'use client'

import type { ChangeEvent } from 'react'
import clsx from 'clsx'

import Error from '/react4xp/components/prototype/Form/Error'
import ErrorCircle from '/react4xp/components/prototype/icons/ErrorCircle'
import { useFormField } from '/react4xp/utils/prototype/useFormField'

import type { InputProps } from './types'

export default function Input({
	id,
	name,
	label,
	ariaLabel,
	type = 'text',
	floatingLabel = false,
	placeholder,
	required = false,
	icon,
	error: externalError,
	invalid: externalInvalid,
	className,
	onChange: externalOnChange,
	requiredMessage,
	invalidEmailMessage,
	...props
}: InputProps) {
	const { finalError, finalInvalid, updateFormValue, getRegisterProps } = useFormField({
		name,
		type,
		externalError,
		externalInvalid,
		required,
		requiredMessage,
		invalidEmailMessage,
	})
	const errorId = `${id}-error`

	const registerProps = getRegisterProps()

	function handleChange(e: ChangeEvent<HTMLInputElement>) {
		externalOnChange?.(e)
		updateFormValue(e.target.value)
	}

	return (
		<div className={clsx('form-field', className)}>
			{label && !floatingLabel && (
				<label htmlFor={id} className="form-label">
					{label}
					{required && (
						<span aria-hidden="true" className="form-label-required">
							*
						</span>
					)}
				</label>
			)}
			<div className="group form-text-field">
				<input
					{...registerProps}
					id={id}
					name={name}
					type={type}
					placeholder={floatingLabel ? placeholder || ' ' : placeholder}
					required={required}
					aria-label={!label ? ariaLabel : undefined}
					aria-invalid={finalInvalid || undefined}
					aria-describedby={finalError ? errorId : undefined}
					className={clsx('form-text-input', floatingLabel && 'peer form-text-input--floating')}
					onChange={handleChange}
					{...props}
				/>
				{label && floatingLabel && (
					<label htmlFor={id} className="form-floating-label">
						{label}
						{required && (
							<span aria-hidden="true" className="ml-0.5">
								*
							</span>
						)}
					</label>
				)}
				{finalError && finalInvalid ? (
					<ErrorCircle className="form-text-error-icon" aria-hidden="true" />
				) : (
					icon && <span className="form-icon">{icon}</span>
				)}
			</div>
			{finalError && finalInvalid && <Error label={finalError} id={errorId} showIcon={false} />}
		</div>
	)
}
